import React, { useCallback, useEffect, useState } from "react";
import {
 Badge,
 Button,
 Card,
 Col,
 Form,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import {
 Bar,
 BarChart,
 CartesianGrid,
 Cell,
 Legend,
 ResponsiveContainer,
 Tooltip,
 XAxis,
 YAxis,
} from "recharts";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

/* 
 COLOUR PALETTE
 */
const COLORS = {
 supervisors: "#0078D7",
 engineers: "#F47920",
 skilled: "#28A745",
 unskilled: "#FFC107",
 total: "#6F42C1",
};

const PROJECT_COLORS = [
 "#0078D7","#F47920","#28A745","#DC3545",
 "#6F42C1","#17A2B8","#FD7E14","#20C997",
 "#E91E63","#546E7A",
];

/* 
 HELPERS
 */
const fmtDate = (v) =>
 v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const toInput = (d) => d ? new Date(d).toISOString().split("T")[0] : "";

/* 
 STAT CARD
 */
const StatCard = ({ label, value, color, icon, sub }) => (
 <Col xs={6} sm={4} xl={2}>
 <Card
 className="border-0 shadow-sm h-100"
 style={{ borderLeft: `4px solid ${color}`, borderRadius: 10 }}
 >
 <Card.Body className="py-3 px-3">
 <div className="d-flex align-items-center gap-2 mb-1">
 <div
 className="rounded-circle d-flex align-items-center justify-content-center"
 style={{ width: 32, height: 32, background: `${color}18` }}
 >
 <i className={`fas ${icon}`} style={{ color, fontSize: 13 }} />
 </div>
 <span className="text-muted small">{label}</span>
 </div>
 <h4 className="fw-bold mb-0" style={{ color }}>
 {value}
 </h4>
 {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
 </Card.Body>
 </Card>
 </Col>
);

/* 
 CUSTOM TOOLTIP
 */
const CustomTooltip = ({ active, payload, label }) => {
 if (!active || !payload?.length) return null;
 return (
 <div
 className="shadow-lg rounded p-2"
 style={{ background: "#fff", border: "1px solid #e2e8f0", fontSize: 12, minWidth: 160 }}
 >
 <div className="fw-semibold mb-1 text-dark">{label}</div>
 {payload.map((p) => (
 <div key={p.dataKey} className="d-flex justify-content-between gap-3">
 <span style={{ color: p.color }}>{p.name}</span>
 <span className="fw-bold">{p.value}</span>
 </div>
 ))}
 </div>
 );
};

/* 
 MAIN COMPONENT
 */
const ManpowerDashboard = () => {
 /* filters */
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [selectedProject, setSelectedProject] = useState("");
 const [selectedWO, setSelectedWO] = useState("");
 const [groupBy, setGroupBy] = useState("day");

 // default: last 30 days
 const today30 = new Date();
 const past30 = new Date();
 past30.setDate(past30.getDate() - 29);
 const [fromDate, setFromDate] = useState(toInput(past30));
 const [toDate, setToDate] = useState(toInput(today30));

 /* data */
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);

 /* chart view toggle */
 const [trendView, setTrendView] = useState("stacked"); // stacked | grouped | total

 /* loaders */
 const loadProjects = useCallback(async () => {
 try {
 const res = await api.get("/projects?limit=500");
 if (res.data?.success) setProjects(res.data.data || []);
 } catch { /* silent */ }
 }, []);

 const loadWorkOrders = useCallback(async () => {
 if (!selectedProject) { setWorkOrders([]); return; }
 try {
 const res = await api.get(`/projects/${selectedProject}/work-orders`);
 if (res.data?.success) setWorkOrders(res.data.data || []);
 } catch { setWorkOrders([]); }
 }, [selectedProject]);

 const loadDashboard = useCallback(async () => {
 setLoading(true);
 try {
 const params = { groupBy };
 if (selectedProject) params.project = selectedProject;
 if (selectedWO) params.workOrder = selectedWO;
 if (fromDate) params.fromDate = fromDate;
 if (toDate) params.toDate = toDate;

 const res = await api.get("/dpr/manpower-dashboard", { params });
 setData(res.data?.data || null);
 } catch (err) {
 console.error("Manpower dashboard load failed", err);
 setData(null);
 } finally {
 setLoading(false);
 }
 }, [selectedProject, selectedWO, groupBy, fromDate, toDate]);

 useEffect(() => { loadProjects(); }, [loadProjects]);
 useEffect(() => { loadWorkOrders(); }, [loadWorkOrders]);
 useEffect(() => { loadDashboard(); }, [loadDashboard]);

 /* quick presets */
 const applyPreset = (days) => {
 const t = new Date();
 const f = new Date();
 f.setDate(f.getDate() - (days - 1));
 setFromDate(toInput(f));
 setToDate(toInput(t));
 };

 const summary = data?.summary || {};
 const category = data?.category || {};
 const trend = data?.trend || [];
 const byProject = data?.byProject || [];
 const peakDays = data?.peakDays || [];

 /* category pie data for bar */
 const categoryBarData = [
 { name: "Supervisors", value: category.supervisors || 0, color: COLORS.supervisors },
 { name: "Engineers", value: category.engineers || 0, color: COLORS.engineers },
 { name: "Skilled", value: category.skilled || 0, color: COLORS.skilled },
 { name: "Unskilled", value: category.unskilled || 0, color: COLORS.unskilled },
 ];

 /* RENDER */
 return (
 <>
 <PageTitle activeMenu="Manpower Dashboard" motherMenu="DPR" />

 {/* Header */}
 <Card className="mb-3 shadow-sm border-0">
 <Card.Body className="py-3">
 <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
 <div>
 <h4 className="fw-bold mb-1">
 <i className="fas fa-hard-hat text-warning me-2" />
 Daily Manpower Deployment Monitoring
 </h4>
 <p className="text-muted mb-0 small">
 Category-wise deployment trends from DPR data — Supervisors, Engineers, Skilled &amp; Unskilled labour.
 </p>
 </div>
 <Button
 size="sm"
 variant="outline-secondary"
 onClick={loadDashboard}
 disabled={loading}
 >
 {loading
 ? <Spinner size="sm" animation="border" />
 : <i className="fas fa-sync-alt" />}
 </Button>
 </div>
 </Card.Body>
 </Card>

 {/* Filters */}
 <Card className="mb-3 shadow-sm border-0">
 <Card.Body className="py-3">
 <Row className="g-2 align-items-end">
 {/* Project */}
 <Col md={3}>
 <Form.Label className="small fw-semibold mb-1">Project</Form.Label>
 <Form.Select
 size="sm"
 value={selectedProject}
 onChange={(e) => { setSelectedProject(e.target.value); setSelectedWO(""); }}
 >
 <option value="">All Projects</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectCode} — {p.projectName}
 </option>
 ))}
 </Form.Select>
 </Col>

 {/* Work Order */}
 <Col md={2}>
 <Form.Label className="small fw-semibold mb-1">Work Order</Form.Label>
 <Form.Select
 size="sm"
 value={selectedWO}
 onChange={(e) => setSelectedWO(e.target.value)}
 disabled={!selectedProject}
 >
 <option value="">All WOs</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </Col>

 {/* From */}
 <Col md={2}>
 <Form.Label className="small fw-semibold mb-1">From</Form.Label>
 <Form.Control
 type="date" size="sm"
 value={fromDate}
 max={toDate || undefined}
 onChange={(e) => setFromDate(e.target.value)}
 />
 </Col>

 {/* To */}
 <Col md={2}>
 <Form.Label className="small fw-semibold mb-1">To</Form.Label>
 <Form.Control
 type="date" size="sm"
 value={toDate}
 min={fromDate || undefined}
 onChange={(e) => setToDate(e.target.value)}
 />
 </Col>

 {/* Group by */}
 <Col md={2}>
 <Form.Label className="small fw-semibold mb-1">Group By</Form.Label>
 <Form.Select
 size="sm"
 value={groupBy}
 onChange={(e) => setGroupBy(e.target.value)}
 >
 <option value="day">Daily</option>
 <option value="week">Weekly</option>
 <option value="month">Monthly</option>
 </Form.Select>
 </Col>

 {/* Presets */}
 <Col xs="auto" className="d-flex gap-1 align-items-end">
 {[
 { label: "7D", days: 7 },
 { label: "30D", days: 30 },
 { label: "90D", days: 90 },
 ].map((p) => (
 <Button
 key={p.label}
 size="sm"
 variant="outline-primary"
 style={{ fontSize: 12, padding: "5px 10px", whiteSpace: "nowrap" }}
 onClick={() => applyPreset(p.days)}
 >
 {p.label}
 </Button>
 ))}
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {loading && !data ? (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 <p className="text-muted small mt-2">Loading manpower data…</p>
 </div>
 ) : !data || (!trend.length && !byProject.length) ? (
 <Card className="shadow-sm border-0">
 <Card.Body className="text-center py-5 text-muted">
 <i className="fas fa-hard-hat fs-2 mb-3 d-block opacity-25" />
 <h6 className="fw-semibold">No manpower data found</h6>
 <p className="small mb-0">Adjust the filters or date range above</p>
 </Card.Body>
 </Card>
 ) : (
 <>
 {/* KPI Summary Row */}
 <Row className="g-2 mb-3">
 <StatCard
 label="Total Man-days"
 value={summary.sumTotal?.toLocaleString() || 0}
 color={COLORS.total}
 icon="fa-users"
 sub={`Across ${summary.totalDPRs || 0} DPR(s)`}
 />
 <StatCard
 label="Daily Average"
 value={summary.avgTotal || 0}
 color="#17A2B8"
 icon="fa-chart-line"
 sub="men / day"
 />
 <StatCard
 label="Peak Deployment"
 value={summary.maxTotal || 0}
 color="#DC3545"
 icon="fa-arrow-up"
 sub="men in a single day"
 />
 <StatCard
 label="Supervisors"
 value={summary.sumSupervisors?.toLocaleString() || 0}
 color={COLORS.supervisors}
 icon="fa-user-tie"
 sub="total man-days"
 />
 <StatCard
 label="Engineers"
 value={summary.sumEngineers?.toLocaleString() || 0}
 color={COLORS.engineers}
 icon="fa-hard-hat"
 sub="total man-days"
 />
 <StatCard
 label="Skilled + Unskilled"
 value={((summary.sumSkilled || 0) + (summary.sumUnskilled || 0)).toLocaleString()}
 color={COLORS.skilled}
 icon="fa-people-carry"
 sub="total man-days"
 />
 </Row>

 {/* Main Trend Chart */}
 <Card className="mb-3 shadow-sm border-0">
 <Card.Header className="bg-white border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
 <span className="fw-semibold">
 <i className="fas fa-chart-bar text-primary me-2" />
 Manpower Deployment Trend
 <span className="text-muted fw-normal small ms-2">
 ({groupBy === "day" ? "Daily" : groupBy === "week" ? "Weekly" : "Monthly"})
 </span>
 </span>
 <div className="d-flex gap-1">
 {[
 { key: "stacked", label: "Stacked" },
 { key: "grouped", label: "Grouped" },
 { key: "total", label: "Total Only" },
 ].map((v) => (
 <Button
 key={v.key}
 size="sm"
 variant={trendView === v.key ? "primary" : "outline-secondary"}
 style={{ fontSize: 12 }}
 onClick={() => setTrendView(v.key)}
 >
 {v.label}
 </Button>
 ))}
 </div>
 </Card.Header>
 <Card.Body style={{ padding: "16px 8px" }}>
 {trend.length === 0 ? (
 <div className="text-center text-muted py-4 small">No trend data</div>
 ) : (
 <ResponsiveContainer width="100%" height={320}>
 <BarChart
 data={trend}
 margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
 barCategoryGap="30%"
 barGap={2}
 >
 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
 <XAxis
 dataKey="label"
 tick={{ fontSize: 11, fill: "#6c757d" }}
 interval={trend.length > 30 ? Math.floor(trend.length / 15) : 0}
 angle={trend.length > 20 ? -35 : 0}
 textAnchor={trend.length > 20 ? "end" : "middle"}
 height={trend.length > 20 ? 50 : 30}
 />
 <YAxis
 tick={{ fontSize: 11, fill: "#6c757d" }}
 allowDecimals={false}
 width={40}
 />
 <Tooltip content={<CustomTooltip />} />
 <Legend
 iconSize={10}
 wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
 />

 {trendView === "total" ? (
 <Bar
 dataKey="total"
 name="Total"
 fill={COLORS.total}
 radius={[3, 3, 0, 0]}
 maxBarSize={40}
 />
 ) : trendView === "stacked" ? (
 <>
 <Bar dataKey="unskilled" name="Unskilled" stackId="a" fill={COLORS.unskilled} maxBarSize={40} />
 <Bar dataKey="skilled" name="Skilled" stackId="a" fill={COLORS.skilled} maxBarSize={40} />
 <Bar dataKey="engineers" name="Engineers" stackId="a" fill={COLORS.engineers} maxBarSize={40} />
 <Bar dataKey="supervisors" name="Supervisors" stackId="a" fill={COLORS.supervisors} radius={[3, 3, 0, 0]} maxBarSize={40} />
 </>
 ) : (
 <>
 <Bar dataKey="supervisors" name="Supervisors" fill={COLORS.supervisors} radius={[3, 3, 0, 0]} maxBarSize={18} />
 <Bar dataKey="engineers" name="Engineers" fill={COLORS.engineers} radius={[3, 3, 0, 0]} maxBarSize={18} />
 <Bar dataKey="skilled" name="Skilled" fill={COLORS.skilled} radius={[3, 3, 0, 0]} maxBarSize={18} />
 <Bar dataKey="unskilled" name="Unskilled" fill={COLORS.unskilled} radius={[3, 3, 0, 0]} maxBarSize={18} />
 </>
 )}
 </BarChart>
 </ResponsiveContainer>
 )}
 </Card.Body>
 </Card>

 <Row className="g-3 mb-3">
 {/* Category Breakdown */}
 <Col lg={5}>
 <Card className="h-100 shadow-sm border-0">
 <Card.Header className="bg-white border-bottom fw-semibold py-3">
 <i className="fas fa-layer-group text-success me-2" />
 Category Breakdown
 </Card.Header>
 <Card.Body style={{ padding: "16px 8px" }}>
 <ResponsiveContainer width="100%" height={220}>
 <BarChart
 data={categoryBarData}
 layout="vertical"
 margin={{ top: 4, right: 24, left: 10, bottom: 4 }}
 barCategoryGap="25%"
 >
 <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
 <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
 <YAxis
 dataKey="name"
 type="category"
 tick={{ fontSize: 12 }}
 width={90}
 />
 <Tooltip content={<CustomTooltip />} />
 <Bar dataKey="value" name="Man-days" radius={[0, 4, 4, 0]} maxBarSize={28}>
 {categoryBarData.map((entry, index) => (
 <Cell key={index} fill={entry.color} />
 ))}
 </Bar>
 </BarChart>
 </ResponsiveContainer>

 {/* Percentage breakdown */}
 <div className="mt-2 px-2">
 {categoryBarData.map((c) => {
 const pct = category.total > 0
 ? Math.round((c.value / category.total) * 100)
 : 0;
 return (
 <div key={c.name} className="d-flex align-items-center mb-1 gap-2">
 <div
 style={{
 width: 10, height: 10, borderRadius: 2,
 background: c.color, flexShrink: 0,
 }}
 />
 <span className="small flex-fill">{c.name}</span>
 <div
 className="rounded"
 style={{
 width: `${pct}%`, minWidth: 4, maxWidth: "50%",
 height: 6, background: c.color, opacity: 0.7,
 }}
 />
 <span
 className="fw-semibold"
 style={{ fontSize: 12, minWidth: 40, textAlign: "right", color: c.color }}
 >
 {pct}%
 </span>
 </div>
 );
 })}
 </div>
 </Card.Body>
 </Card>
 </Col>

 {/* Per-Project Bar Chart */}
 <Col lg={7}>
 <Card className="h-100 shadow-sm border-0">
 <Card.Header className="bg-white border-bottom fw-semibold py-3">
 <i className="fas fa-project-diagram text-primary me-2" />
 Project-wise Deployment
 </Card.Header>
 <Card.Body style={{ padding: "16px 8px" }}>
 {byProject.length === 0 ? (
 <div className="text-center text-muted py-4 small">No project data</div>
 ) : (
 <ResponsiveContainer width="100%" height={260}>
 <BarChart
 data={byProject.slice(0, 10)}
 margin={{ top: 4, right: 16, left: 0, bottom: 40 }}
 barCategoryGap="30%"
 >
 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
 <XAxis
 dataKey="projectCode"
 tick={{ fontSize: 11, fill: "#6c757d" }}
 angle={-30}
 textAnchor="end"
 height={55}
 />
 <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
 <Tooltip
 content={({ active, payload, label }) => {
 if (!active || !payload?.length) return null;
 const d = payload[0]?.payload;
 return (
 <div
 className="shadow-lg rounded p-2"
 style={{ background: "#fff", border: "1px solid #e2e8f0", fontSize: 12 }}
 >
 <div className="fw-semibold mb-1">{d?.projectName}</div>
 <div>Total: <strong>{d?.total}</strong></div>
 <div>Supervisors: {d?.supervisors} | Engineers: {d?.engineers}</div>
 <div>Skilled: {d?.skilled} | Unskilled: {d?.unskilled}</div>
 <div className="text-muted mt-1">{d?.dprCount} DPR(s)</div>
 </div>
 );
 }}
 />
 <Bar dataKey="total" name="Total Manpower" radius={[3, 3, 0, 0]} maxBarSize={36}>
 {byProject.slice(0, 10).map((_, i) => (
 <Cell key={i} fill={PROJECT_COLORS[i % PROJECT_COLORS.length]} />
 ))}
 </Bar>
 </BarChart>
 </ResponsiveContainer>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Row className="g-3">
 {/* Peak Deployment Days */}
 <Col lg={6}>
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white border-bottom fw-semibold py-3">
 <i className="fas fa-fire text-danger me-2" />
 Peak Deployment Days (Top 5)
 </Card.Header>
 <Card.Body className="p-0">
 <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light">
 <tr>
 <th className="ps-3" style={{ width: 30 }}>#</th>
 <th style={{ width: 110 }}>Date</th>
 <th style={{ width: 130 }}>Project</th>
 <th style={{ minWidth: 160 }}>Work Order</th>
 <th className="text-center" style={{ width: 50 }}>Sup</th>
 <th className="text-center" style={{ width: 50 }}>Eng</th>
 <th className="text-center" style={{ width: 65 }}>Skilled</th>
 <th className="text-center" style={{ width: 75 }}>Unskilled</th>
 <th className="text-center fw-bold" style={{ width: 60 }}>Total</th>
 </tr>
 </thead>
 <tbody>
 {peakDays.map((d, i) => (
 <tr key={d._id}>
 <td className="ps-3 text-muted">{i + 1}</td>
 <td>
 <span className="fw-semibold">{fmtDate(d.date)}</span>
 <div className="text-muted" style={{ fontSize: 10 }}>{d.dprNumber}</div>
 </td>
 <td className="text-muted small">{d.projectName}</td>
 <td>
 <code style={{ fontSize: 10 }}>
 {typeof d.woNo === "string" ? d.woNo : d.woNo?.woNo || "—"}
 </code>
 </td>
 <td className="text-center">{d.manpower?.supervisors ?? 0}</td>
 <td className="text-center">{d.manpower?.engineers ?? 0}</td>
 <td className="text-center">{d.manpower?.skilled ?? 0}</td>
 <td className="text-center">{d.manpower?.unskilled ?? 0}</td>
 <td className="text-center">
 <Badge bg="primary" style={{ fontSize: 11 }}>
 {d.manpower?.total ?? 0}
 </Badge>
 </td>
 </tr>
 ))}
 {peakDays.length === 0 && (
 <tr>
 <td colSpan={9} className="text-center text-muted py-3">No data</td>
 </tr>
 )}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>

 {/* Project-wise Table */}
 <Col lg={6}>
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white border-bottom fw-semibold py-3">
 <i className="fas fa-table text-info me-2" />
 Project-wise Summary Table
 </Card.Header>
 <Card.Body className="p-0">
 <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light">
 <tr>
 <th className="ps-3">Project</th>
 <th className="text-center">DPRs</th>
 <th className="text-center">Sup</th>
 <th className="text-center">Eng</th>
 <th className="text-center">Skilled</th>
 <th className="text-center">Unskilled</th>
 <th className="text-center fw-bold">Total</th>
 </tr>
 </thead>
 <tbody>
 {byProject.map((p, i) => (
 <tr key={p._id}>
 <td className="ps-3">
 <div className="fw-semibold" style={{ fontSize: 12 }}>
 <span
 className="d-inline-block me-1"
 style={{
 width: 8, height: 8, borderRadius: "50%",
 background: PROJECT_COLORS[i % PROJECT_COLORS.length],
 }}
 />
 {p.projectCode}
 </div>
 <div className="text-muted" style={{ fontSize: 10 }}>
 {p.projectName.length > 28
 ? p.projectName.slice(0, 28) + "…"
 : p.projectName}
 </div>
 </td>
 <td className="text-center text-muted">{p.dprCount}</td>
 <td className="text-center" style={{ color: COLORS.supervisors }}>{p.supervisors}</td>
 <td className="text-center" style={{ color: COLORS.engineers }}>{p.engineers}</td>
 <td className="text-center" style={{ color: COLORS.skilled }}>{p.skilled}</td>
 <td className="text-center" style={{ color: COLORS.unskilled }}>{p.unskilled}</td>
 <td className="text-center">
 <Badge
 style={{
 fontSize: 11,
 background: PROJECT_COLORS[i % PROJECT_COLORS.length],
 }}
 >
 {p.total}
 </Badge>
 </td>
 </tr>
 ))}
 {byProject.length === 0 && (
 <tr>
 <td colSpan={7} className="text-center text-muted py-3">No data</td>
 </tr>
 )}
 </tbody>
 {byProject.length > 0 && (
 <tfoot className="table-secondary fw-bold" style={{ fontSize: 13 }}>
 <tr>
 <td className="ps-3">TOTAL</td>
 <td className="text-center">
 {byProject.reduce((s, p) => s + p.dprCount, 0)}
 </td>
 <td className="text-center">
 {byProject.reduce((s, p) => s + p.supervisors, 0)}
 </td>
 <td className="text-center">
 {byProject.reduce((s, p) => s + p.engineers, 0)}
 </td>
 <td className="text-center">
 {byProject.reduce((s, p) => s + p.skilled, 0)}
 </td>
 <td className="text-center">
 {byProject.reduce((s, p) => s + p.unskilled, 0)}
 </td>
 <td className="text-center">
 <Badge bg="dark" style={{ fontSize: 11 }}>
 {byProject.reduce((s, p) => s + p.total, 0)}
 </Badge>
 </td>
 </tr>
 </tfoot>
 )}
 </Table>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </>
 )}
 </>
 );
};

export default ManpowerDashboard;
